import { Controller } from "@hotwired/stimulus"

// A click-to-open property popover (Plane-style), with optional search filtering.
//
//   <div data-controller="popover">
//     <button data-action="popover#toggle" ...>trigger pill</button>
//     <div data-popover-target="panel" hidden>
//       <input data-popover-target="search" data-action="input->popover#filter">
//       <form ...>
//         <label data-popover-target="option" data-popover-name="Jane">
//           <input type="checkbox" data-action="change->popover#markDirty">  (multi)
//           <input type="radio"    data-action="change->popover#submitNow"> (single)
//         </label>
//       </form>
//     </div>
//   </div>
//
// Single-select radios submit immediately (and the redirect_back reloads the page,
// which closes the popover). Multi-select checkboxes defer: we submit once on close
// so you can toggle several before the round-trip.
//
// FD-892: embedded mode (data-popover-embedded-value="true", rendered by
// shared/_user_picker). The radios/checkboxes belong to the SURROUNDING form — a
// epic's Lead, a template's default assignees, the Filters menu — so picking never
// submits here. The input's own native `change` still bubbles to that form, which is
// what keeps an `onchange: requestSubmit` form working; this controller only copies
// the choice into the trigger and closes a single-select.
//
// Search matches data-popover-search (name + email, see UserPickerHelper) when a row
// carries one, else data-popover-name. Up/Down move a highlight through the visible
// rows and Enter picks it, from the search box or anywhere in the open panel.
export default class extends Controller {
  static targets = ["panel", "search", "option", "trigger", "triggerLabel"]
  static values = { embedded: Boolean, placeholder: String }

  connect() {
    this.dirty = false
    this._onDocClick = this.onDocClick.bind(this)
    this._onKeydown = this.onKeydown.bind(this)
  }

  disconnect() {
    this.teardown()
  }

  toggle(event) {
    event.preventDefault()
    this.panelTarget.hidden ? this.open() : this.close()
  }

  open() {
    // FD-641: freeze the whole popover against live morphs while it is open —
    // the same trick inline_edit / owner_picker use. A broadcast refresh landing
    // mid-gesture would otherwise re-render the checkboxes from persisted state,
    // silently discarding the user's in-flight toggles (and re-hiding the panel);
    // close() fires the deferred submit with whatever the morph left checked.
    // Needs the stable id _popover_select renders on this element.
    //
    // The trade-off, deliberately the same one owner_picker_controller makes: the
    // freeze also hides another actor's concurrent change to this property, and
    // the deferred submit then posts the frozen set over it. That window is the
    // time the panel is open, and it costs the other actor's edit rather than the
    // user's own in-flight clicks. Narrowing it further needs a conflict signal
    // (inline_edit's lockVersion banner) rendered outside the frozen node.
    this.element.setAttribute("data-turbo-permanent", "")
    this.panelTarget.hidden = false
    // FD-274 D10: edge-flip. The panel is `position:absolute; left-0`, opening
    // rightward from its trigger. A trigger near the right edge of a clipping
    // container (the peek panel's overflow-y-auto coerces overflow-x to clip)
    // would push the panel off-screen and crop it. After layout settles, if the
    // left-anchored panel overflows the viewport right edge, anchor it right.
    this.panelTarget.classList.remove("fd-popover--flip")
    requestAnimationFrame(() => {
      if (this.panelTarget.hidden) return
      const right = this.panelTarget.getBoundingClientRect().right
      if (right > document.documentElement.clientWidth - 8) {
        this.panelTarget.classList.add("fd-popover--flip")
      }
    })
    document.addEventListener("click", this._onDocClick, true)
    document.addEventListener("keydown", this._onKeydown, true)
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.filter()
      // focus after the click that opened us settles. FD-1190: the search box sits just
      // under the trigger the user clicked, so don't scroll the page to it.
      requestAnimationFrame(() => this.searchTarget.focus({ preventScroll: true }))
    } else {
      this.setActive(-1)
    }
    // FD-502: let a co-located frame-reveal controller lazy-load the option list
    // on first open (data-action="popover:open->frame-reveal#reveal").
    this.dispatch("open")
  }

  close() {
    if (this.panelTarget.hidden) return
    this.panelTarget.hidden = true
    // Drop the freeze BEFORE the deferred submit, so our own write's
    // redirect_back morph updates the trigger pill (mirrors owner_picker).
    this.element.removeAttribute("data-turbo-permanent")
    this.teardown()
    if (this.dirty) {
      this.dirty = false
      const form = this.panelTarget.querySelector("form")
      if (form) form.requestSubmit()
    }
  }

  teardown() {
    document.removeEventListener("click", this._onDocClick, true)
    document.removeEventListener("keydown", this._onKeydown, true)
  }

  // Multi-select: remember a change happened, submit on close. Embedded: the
  // surrounding form owns submitting, so only reflect the choice in the trigger.
  markDirty() {
    if (this.embeddedValue) return this.syncTrigger()
    this.dirty = true
  }

  // Single-select: submit right away and close. (The redirect's morph used to
  // close the popover by re-rendering it hidden; the FD-641 freeze makes it
  // skip the open popover, so close explicitly — which also drops the freeze
  // in time for that morph to update the trigger pill.) Embedded: no submit.
  submitNow(event) {
    if (this.embeddedValue) {
      this.syncTrigger()
    } else {
      event.target.closest("form")?.requestSubmit()
    }
    if (this.hasPanelTarget) this.close()
  }

  filter() {
    if (!this.hasSearchTarget) return
    const query = this.searchTarget.value.trim().toLowerCase()
    this.optionTargets.forEach((option) => {
      const text = (option.dataset.popoverSearch || option.dataset.popoverName || "").toLowerCase()
      option.hidden = query !== "" && !text.includes(query)
    })
    // Typing narrows the list, and narrowing is the act of choosing: highlight the
    // first match so Enter takes it. An empty query highlights nothing, so Enter on
    // an untouched list never picks whoever sorts first (owner_picker's FD-615 rule).
    this.setActive(query === "" ? -1 : 0)
  }

  // --- keyboard ----------------------------------------------------------------

  // Bound once, on the element that holds the search box AND the rows (the panel,
  // or the list wrapper in shared/_user_picker's always-open list mode), so a key
  // pressed in the search box reaches it exactly once.
  navigate(event) {
    if (event.defaultPrevented) return
    const visible = this.visibleOptions()
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault()
        if (visible.length === 0) return
        const step = event.key === "ArrowDown" ? 1 : -1
        const current = visible.indexOf(this.activeOption)
        const next = current === -1 ? (step === 1 ? 0 : visible.length - 1)
          : (current + step + visible.length) % visible.length
        this.setActive(next)
        break
      }
      case "Enter": {
        // Never let Enter in the search box fall through to the enclosing form: in
        // the epic form that would save the epic off a half-typed name.
        if ((this.hasSearchTarget && event.target === this.searchTarget) || this.activeOption) event.preventDefault()
        if (this.activeOption && !this.activeOption.hidden) this.choose(this.activeOption)
        break
      }
    }
  }

  visibleOptions() {
    return this.optionTargets.filter((option) => !option.hidden)
  }

  // index into visibleOptions(); -1 clears the highlight.
  setActive(index) {
    this.optionTargets.forEach((option) => option.classList.remove("fd-popover-opt--active"))
    this.activeOption = this.visibleOptions()[index] || null
    if (!this.activeOption) return
    this.activeOption.classList.add("fd-popover-opt--active")
    this.#revealActive()
  }

  // FD-1190: keep the highlighted row visible by scrolling ONLY the popover's own list.
  // scrollIntoView also scrolls every scrolling ancestor (the page pane, the peek drawer),
  // which nudged the page on each keystroke while filtering.
  #revealActive() {
    const option = this.activeOption
    const list = this.#scrollingList(option)
    if (!list) return
    const row = option.getBoundingClientRect()
    const top = list.getBoundingClientRect().top + list.clientTop
    const bottom = top + list.clientHeight
    if (row.top < top) list.scrollTop -= top - row.top
    else if (row.bottom > bottom) list.scrollTop += row.bottom - bottom
  }

  // The nearest scrolling ancestor of `option` that is still inside this popover.
  #scrollingList(option) {
    for (let node = option.parentElement; node && this.element.contains(node); node = node.parentElement) {
      if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node
    }
    return null
  }

  // Pick a row the way a click would. click() on the row's input fires the native
  // `change` (which is what submitNow/markDirty and any form-level onchange hear).
  // A radio that is already chosen fires nothing, so just close.
  choose(option) {
    const input = option.querySelector("input[type=checkbox], input[type=radio]")
    if (!input) return
    if (input.type === "radio" && input.checked) {
      if (this.hasPanelTarget) this.close()
      return
    }
    input.click()
  }

  // Embedded mode: mirror the checked row(s) into the trigger. A single pick also
  // copies the row's avatar (its [data-picker-dot]) so the trigger looks exactly
  // like the server-rendered one.
  syncTrigger() {
    if (!this.hasTriggerLabelTarget) return
    const checked = this.optionTargets.filter((option) => {
      const input = option.querySelector("input:checked")
      return input && input.value !== ""
    })
    const label = this.triggerLabelTarget
    if (this.hasTriggerTarget) this.triggerTarget.querySelectorAll("[data-picker-dot]").forEach((dot) => dot.remove())
    if (checked.length === 0) {
      label.textContent = this.placeholderValue
      label.classList.add("text-muted")
      return
    }
    label.textContent = checked.map((option) => option.dataset.popoverName).join(", ")
    label.classList.remove("text-muted")
    const dot = checked[0].querySelector("[data-picker-dot]")
    if (dot && this.hasTriggerTarget) this.triggerTarget.insertBefore(dot.cloneNode(true), label)
  }

  onDocClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      event.stopPropagation()
      this.close()
    }
  }
}
